import { createSlice, type Slice, type SliceCaseReducers, type PayloadAction } from '@reduxjs/toolkit';
import { createMigrate, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import type { ViewSlug } from '~/types/components';
import type { Call, Campaign, Contact, Email, Group, Sms } from '~/types/models';
import { restSliceMigrations } from '~/types/states';
import type { INestedRestSlice, IRestSlice, IRestIndexParams } from '~/types/states';
import type { StringKeys } from '~/types/utils';

export function createContactsSlice(prefix: string): {
  actions: Slice<INestedRestSlice<Contact>, SliceCaseReducers<INestedRestSlice<Contact>>, string>['actions'];
  reducer: Slice<INestedRestSlice<Contact>, SliceCaseReducers<INestedRestSlice<Contact>>, string>['reducer'];
  state: INestedRestSlice<Contact>;
} {
  const initialState: INestedRestSlice<Contact> = {
    context: '',
    columns: {
      index: [
        'sequence',
        'firstName',
        'lastName',
        'postalCode',
        'cellPhoneNumber',
        'email',
        'lastResult',
        'groups',
        'lastUpdated',
      ],
      single: [
        'sequence',
        'firstName',
        'lastName',
        'postalCode',
        'cellPhoneNumber',
        'email',
        'lastResult',
        'groups',
        'lastUpdated',
      ],
    },
    data: [],
    error: null,
    filters: {},
    initialItem: null,
    item: null,
    loading: true,
    page: 1,
    pageSize: 10,
    sortBy: '-sequence',
    total: null,
    totalPages: null,
    with: [],
  };

  const reducers: SliceCaseReducers<INestedRestSlice<Contact>> = {
    addContactsToCampaign: (
      _state,
      _action: PayloadAction<{
        campaign: Campaign;
        lockedFilters?: IRestSlice<Contact>['filters'];
      }>,
    ) => {},
    addContactsToGroup: (
      _state,
      _action: PayloadAction<{
        group: Group;
        lockedFilters?: IRestSlice<Contact>['filters'];
      }>,
    ) => {},
    index: (_state, _action: PayloadAction<IRestIndexParams<Contact>>) => {},
    removeContactsFromCampaign: (
      _state,
      _action: PayloadAction<{
        campaign: Campaign;
        lockedFilters?: IRestSlice<Contact>['filters'];
      }>,
    ) => {},
    removeContactsFromGroup: (
      _state,
      _action: PayloadAction<{
        group: Group;
        lockedFilters?: IRestSlice<Contact>['filters'];
      }>,
    ) => {},
    reset: (state) => {
      state.data.splice(0, state.data.length);
      state.loading = false;
      state.pageSize = 10;
      state.page = 1;
      state.filters = {};
    },
    retrieve: (_state, _action: PayloadAction<{ id: Contact['id']; params?: IRestIndexParams<Contact> }>) => {},
    setColumns(
      state,
      { payload: { view, columns } }: PayloadAction<{ view: ViewSlug; columns: Array<StringKeys<Contact>> }>,
    ) {
      state.columns[view].splice(0, state.columns[view].length, ...columns);

      if (state.columns[view].length === 0) {
        state.columns[view].splice(0, state.columns[view].length, ...initialState.columns[view]);
      }
    },
    setContext(state, { payload }) {
      state.context = payload;
    },
    setData(state, { payload }) {
      state.data.splice(0, state.data.length, ...payload);
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        key: keyof Contact;
        value: string | number | string[];
      }>,
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setFilters: (state, { payload }) => {
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null) {
          delete state.filters[k as keyof Contact];
        } else {
          state.filters[k as keyof Contact] = payload[k];
        }
      });
    },
    setInitialItem: (state, { payload }) => {
      state.initialItem = payload;
    },
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    setParams: (_state) => {},
    setSortBy: (state, { payload }) => {
      state.sortBy = payload;
    },
    setTotal: (state, { payload }) => {
      state.total = payload;
    },
    setTotalPages: (state, { payload }) => {
      state.totalPages = payload;
    },
    setWith: (state, { payload }: PayloadAction<string[]>) => {
      state.with = payload;
    },
    unsetFilter: (state, action: PayloadAction<keyof Contact>) => {
      delete state.filters[action.payload];
    },
    update: (_state, _action: PayloadAction<Contact>) => {},
  };

  const contactsSlice = createSlice({
    name: `${prefix}/contacts`,
    initialState,
    reducers,
  });

  const { actions, reducer: contactsReducer, getInitialState } = contactsSlice;

  const casePrefix = prefix.substring(0, 1).toUpperCase() + prefix.substring(1);
  const reducer = persistReducer(
    {
      key: `CSN_Pointage:${casePrefix}Contacts`,
      migrate: createMigrate(restSliceMigrations),
      storage,
      version: 0,
      whitelist: ['columns', 'filters', 'page', 'pageSize', 'sortBy', 'with'],
    },
    contactsReducer,
  ) as any;

  return { actions, reducer, state: getInitialState() };
}

export function createCallsSlice(prefix: string): {
  actions: Slice<INestedRestSlice<Call>, SliceCaseReducers<INestedRestSlice<Call>>, string>['actions'];
  reducer: Slice<INestedRestSlice<Call>, SliceCaseReducers<INestedRestSlice<Call>>, string>['reducer'];
  state: INestedRestSlice<Call>;
} {
  const initialState: INestedRestSlice<Call> = {
    context: '',
    columns: {
      index: ['campaign', 'comment', 'contact', 'group', 'user', 'lastUpdated', 'result', 'sequence', 'status', 'tags'],
      single: [
        'campaign',
        'comment',
        'contact',
        'group',
        'lastUpdated',
        'result',
        'sequence',
        'status',
        'tags',
        'user',
      ],
    },
    data: [],
    error: null,
    filters: {},
    initialItem: null,
    item: null,
    loading: true,
    page: 1,
    pageSize: 10,
    sortBy: '-sequence',
    total: null,
    totalPages: null,
    with: [],
  };

  const reducers: SliceCaseReducers<INestedRestSlice<Call>> = {
    index: (_state, _action: PayloadAction<IRestIndexParams<Call>>) => {},
    reset: (state) => {
      state.data.splice(0, state.data.length);
      state.loading = false;
      state.pageSize = 10;
      state.page = 1;
      state.filters = {};
    },
    retrieve: (_state, _action: PayloadAction<{ id: Call['id']; params?: IRestIndexParams<Call> }>) => {},
    setColumns(
      state,
      { payload: { view, columns } }: PayloadAction<{ view: ViewSlug; columns: Array<StringKeys<Call>> }>,
    ) {
      state.columns[view].splice(0, state.columns[view].length, ...columns);

      if (state.columns[view].length === 0) {
        state.columns[view].splice(0, state.columns[view].length, ...initialState.columns[view]);
      }
    },
    setContext(state, { payload }) {
      state.context = payload;
    },
    setData(state, { payload }) {
      state.data.splice(0, state.data.length, ...payload);
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        key: keyof Call;
        value: string | number | string[];
      }>,
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setFilters: (state, { payload }) => {
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null) {
          delete state.filters[k as keyof Call];
        } else {
          state.filters[k as keyof Call] = payload[k];
        }
      });
    },
    setInitialItem: (state, { payload }) => {
      state.initialItem = payload;
    },
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    setParams: (_state) => {},
    setSortBy: (state, { payload }) => {
      state.sortBy = payload;
    },
    setTotal: (state, { payload }) => {
      state.total = payload;
    },
    setTotalPages: (state, { payload }) => {
      state.totalPages = payload;
    },
    setWith: (state, { payload }: PayloadAction<string[]>) => {
      state.with = payload;
    },
    unsetFilter: (state, action: PayloadAction<keyof Call>) => {
      delete state.filters[action.payload];
    },
    update: (_state, _action: PayloadAction<Call>) => {},
  };

  const callsSlice = createSlice({
    name: `${prefix}/calls`,
    initialState,
    reducers,
  });

  const { actions, reducer: callsReducer, getInitialState } = callsSlice;

  const casePrefix = prefix.substring(0, 1).toUpperCase() + prefix.substring(1);
  const reducer = persistReducer(
    {
      key: `CSN_Pointage:${casePrefix}Calls`,
      migrate: createMigrate(restSliceMigrations),
      storage,
      version: 0,
      whitelist: ['columns', 'filters', 'page', 'pageSize', 'sortBy', 'with'],
    },
    callsReducer,
  ) as any;

  return { actions, reducer, state: getInitialState() };
}

export function createEmailsSlice(prefix: string): {
  actions: Slice<INestedRestSlice<Email>, SliceCaseReducers<INestedRestSlice<Email>>, string>['actions'];
  reducer: Slice<INestedRestSlice<Email>, SliceCaseReducers<INestedRestSlice<Email>>, string>['reducer'];
  state: INestedRestSlice<Email>;
} {
  const initialState: INestedRestSlice<Email> = {
    context: '',
    columns: {
      index: ['campaign', 'contact', 'lastUpdated', 'lastResult', 'sequence', 'tags'],
      single: ['campaign', 'contact', 'lastUpdated', 'lastResult', 'sequence', 'tags'],
    },
    data: [],
    error: null,
    filters: {},
    initialItem: null,
    item: null,
    loading: true,
    page: 1,
    pageSize: 10,
    sortBy: '-sequence',
    total: null,
    totalPages: null,
    with: [],
  };

  const reducers: SliceCaseReducers<INestedRestSlice<Email>> = {
    index: (_state, _action: PayloadAction<IRestIndexParams<Email>>) => {},
    reset: (state) => {
      state.data.splice(0, state.data.length);
      state.loading = false;
      state.pageSize = 10;
      state.page = 1;
      state.filters = {};
    },
    retrieve: (_state, _action: PayloadAction<{ id: Email['id']; params?: IRestIndexParams<Email> }>) => {},
    setColumns(
      state,
      { payload: { view, columns } }: PayloadAction<{ view: ViewSlug; columns: Array<StringKeys<Email>> }>,
    ) {
      state.columns[view].splice(0, state.columns[view].length, ...columns);

      if (state.columns[view].length === 0) {
        state.columns[view].splice(0, state.columns[view].length, ...initialState.columns[view]);
      }
    },
    setContext(state, { payload }) {
      state.context = payload;
    },
    setData(state, { payload }) {
      state.data.splice(0, state.data.length, ...payload);
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        key: keyof Email;
        value: string | number | string[];
      }>,
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setFilters: (state, { payload }) => {
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null) {
          delete state.filters[k as keyof Email];
        } else {
          state.filters[k as keyof Email] = payload[k];
        }
      });
    },
    setInitialItem: (state, { payload }) => {
      state.initialItem = payload;
    },
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    setParams: (_state) => {},
    setSortBy: (state, { payload }) => {
      state.sortBy = payload;
    },
    setTotal: (state, { payload }) => {
      state.total = payload;
    },
    setTotalPages: (state, { payload }) => {
      state.totalPages = payload;
    },
    setWith: (state, { payload }: PayloadAction<string[]>) => {
      state.with = payload;
    },
    unsetFilter: (state, action: PayloadAction<keyof Email>) => {
      delete state.filters[action.payload];
    },
    update: (_state, _action: PayloadAction<Email>) => {},
  };

  const emailsSlice = createSlice({
    name: `${prefix}/emails`,
    initialState,
    reducers,
  });

  const { actions, reducer: emailsReducer, getInitialState } = emailsSlice;

  const casePrefix = prefix.substring(0, 1).toUpperCase() + prefix.substring(1);
  const reducer = persistReducer(
    {
      key: `CSN_Pointage:${casePrefix}Emails`,
      migrate: createMigrate(restSliceMigrations),
      storage,
      version: 0,
      whitelist: ['columns', 'filters', 'page', 'pageSize', 'sortBy', 'with'],
    },
    emailsReducer,
  ) as any;

  return { actions, reducer, state: getInitialState() };
}

export function createSmsSlice(prefix: string): {
  actions: Slice<INestedRestSlice<Sms>, SliceCaseReducers<INestedRestSlice<Sms>>, string>['actions'];
  reducer: Slice<INestedRestSlice<Sms>, SliceCaseReducers<INestedRestSlice<Sms>>, string>['reducer'];
  state: INestedRestSlice<Sms>;
} {
  const initialState: INestedRestSlice<Sms> = {
    context: '',
    columns: {
      index: ['campaign', 'contact', 'lastUpdated', 'lastResult', 'sequence', 'tags'],
      single: ['campaign', 'contact', 'lastUpdated', 'lastResult', 'sequence', 'tags'],
    },
    data: [],
    error: null,
    filters: {},
    initialItem: null,
    item: null,
    loading: true,
    page: 1,
    pageSize: 10,
    sortBy: '-sequence',
    total: null,
    totalPages: null,
    with: [],
  };

  const reducers: SliceCaseReducers<INestedRestSlice<Sms>> = {
    index: (_state, _action: PayloadAction<IRestIndexParams<Sms>>) => {},
    reset: (state) => {
      state.data.splice(0, state.data.length);
      state.loading = false;
      state.pageSize = 10;
      state.page = 1;
      state.filters = {};
    },
    retrieve: (_state, _action: PayloadAction<{ id: Sms['id']; params?: IRestIndexParams<Sms> }>) => {},
    setColumns(
      state,
      { payload: { view, columns } }: PayloadAction<{ view: ViewSlug; columns: Array<StringKeys<Sms>> }>,
    ) {
      state.columns[view].splice(0, state.columns[view].length, ...columns);

      if (state.columns[view].length === 0) {
        state.columns[view].splice(0, state.columns[view].length, ...initialState.columns[view]);
      }
    },
    setContext(state, { payload }) {
      state.context = payload;
    },
    setData(state, { payload }) {
      state.data.splice(0, state.data.length, ...payload);
    },
    setError(state, { payload }) {
      state.error = payload;
    },
    setFilter: (
      state,
      action: PayloadAction<{
        key: keyof Sms;
        value: string | number | string[];
      }>,
    ) => {
      state.filters[action.payload.key] = action.payload.value;
    },
    setFilters: (state, { payload }) => {
      Object.keys(payload).forEach((k) => {
        if (payload[k] === null) {
          delete state.filters[k as keyof Sms];
        } else {
          state.filters[k as keyof Sms] = payload[k];
        }
      });
    },
    setInitialItem: (state, { payload }) => {
      state.initialItem = payload;
    },
    setItem: (state, { payload }) => {
      state.item = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    setPageSize: (state, { payload }) => {
      state.pageSize = payload;
    },
    setParams: (_state) => {},
    setSortBy: (state, { payload }) => {
      state.sortBy = payload;
    },
    setTotal: (state, { payload }) => {
      state.total = payload;
    },
    setTotalPages: (state, { payload }) => {
      state.totalPages = payload;
    },
    setWith: (state, { payload }: PayloadAction<string[]>) => {
      state.with = payload;
    },
    unsetFilter: (state, action: PayloadAction<keyof Sms>) => {
      delete state.filters[action.payload];
    },
    update: (_state, _action: PayloadAction<Sms>) => {},
  };

  const smsSlice = createSlice({
    name: `${prefix}/sms`,
    initialState,
    reducers,
  });

  const { actions, reducer: smsReducer, getInitialState } = smsSlice;

  const casePrefix = prefix.substring(0, 1).toUpperCase() + prefix.substring(1);
  const reducer = persistReducer(
    {
      key: `CSN_Pointage:${casePrefix}Sms`,
      migrate: createMigrate(restSliceMigrations),
      storage,
      version: 0,
      whitelist: ['columns', 'filters', 'page', 'pageSize', 'sortBy', 'with'],
    },
    smsReducer,
  ) as any;

  return { actions, reducer, state: getInitialState() };
}
